import { useId } from 'react';
import { cn } from '@/lib/cn';

/**
 * Минимални графики, нарисувани на ръка. Никаква библиотека: справките са
 * четири екрана с по една линия, а всяка графична библиотека тежи повече от
 * целия този борд. Цветовете идват от `currentColor`, затова работят и в
 * двете теми, а описанието се чете от екранен четец.
 */
export function Sparkline({
  values,
  className,
  width = 120,
  height = 28,
  label,
}: {
  values: number[];
  className?: string;
  width?: number;
  height?: number;
  label: string;
}) {
  const gradientId = useId();
  if (values.length < 2) {
    return <div className={cn('h-7 text-xs text-ink-faint', className)}>—</div>;
  }

  const min = Math.min(...values, 0);
  const max = Math.max(...values, 0);
  const span = max - min || 1;
  const step = width / (values.length - 1);

  const points = values.map((value, index) => {
    const x = index * step;
    const y = height - ((value - min) / span) * height;
    return [x, Number.isFinite(y) ? y : height] as const;
  });

  const line = points
    .map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`)
    .join(' ');
  const area = `${line} L${width},${height} L0,${height} Z`;
  const last = points[points.length - 1];

  return (
    <svg
      className={cn('text-accent', className)}
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      role="img"
      aria-label={label}
      preserveAspectRatio="none"
    >
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="currentColor" stopOpacity="0.28" />
          <stop offset="100%" stopColor="currentColor" stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={area} fill={`url(#${gradientId})`} />
      <path
        d={line}
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        vectorEffect="non-scaling-stroke"
      />
      {last ? <circle cx={last[0]} cy={last[1]} r="2" fill="currentColor" /> : null}
    </svg>
  );
}

export interface BarDatum {
  label: string;
  value: number;
  secondary?: number;
}

/**
 * Стълбовидна графика по месеци. Втората стойност (маржът) се рисува върху
 * първата (прихода) — така се вижда какво остава, а не само колко е влязло.
 */
export function BarChart({
  data,
  height = 140,
  formatValue,
  label,
  className,
}: {
  data: BarDatum[];
  height?: number;
  formatValue: (value: number) => string;
  label: string;
  className?: string;
}) {
  if (data.length === 0) return <div className="py-8 text-center text-xs text-ink-faint">—</div>;
  const max = Math.max(...data.map((d) => Math.max(d.value, d.secondary ?? 0)), 1);

  return (
    <figure className={cn('w-full', className)}>
      <figcaption className="sr-only">{label}</figcaption>
      {/*
        Колоните трябва да се разтеглят на цялата височина: с `items-end` те
        нямат собствено съдържание, свиват се до нула и процентите на стълбовете
        се смятат спрямо нулева височина — графиката излиза празна.
      */}
      <div className="flex items-stretch gap-1" style={{ height }}>
        {data.map((datum) => {
          const primaryPct = (datum.value / max) * 100;
          const secondaryPct = datum.secondary === undefined ? 0 : (datum.secondary / max) * 100;
          return (
            <div
              key={datum.label}
              className="group relative flex min-w-0 flex-1 flex-col justify-end"
              title={`${datum.label}: ${formatValue(datum.value)}${
                datum.secondary === undefined ? '' : ` · ${formatValue(datum.secondary)}`
              }`}
            >
              <div
                className="w-full rounded-t bg-line-strong transition-colors group-hover:bg-ink-faint"
                style={{ height: `${Math.max(primaryPct, 1)}%` }}
              >
                {datum.secondary !== undefined ? (
                  <div
                    className="absolute bottom-0 w-full rounded-t bg-accent"
                    style={{ height: `${Math.max(secondaryPct, 0)}%` }}
                  />
                ) : null}
              </div>
            </div>
          );
        })}
      </div>
      <div className="mt-1 flex gap-1">
        {data.map((datum) => (
          <div
            key={datum.label}
            className="min-w-0 flex-1 truncate text-center text-[10px] text-ink-faint"
          >
            {datum.label}
          </div>
        ))}
      </div>
    </figure>
  );
}
